import { FunctionComponent, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  getManagementChartTooltipProps,
  getManagementNeutralChartFill
} from "../utils/management-chart-theme.utils";

export type ManagementDonutSegment = {
  name: string;
  value: number;
  color: string;
};

type ManagementDonutChartProps = {
  centerLabel: string;
  centerSublabel?: string;
  valueSuffix?: string;
  /** Larger pie for the combined Today panel. */
  size?: "default" | "medium" | "large";
  testId?: string;
  /** Multi-segment pie (e.g. office / mobile / not specified). */
  segments?: ManagementDonutSegment[];
  /** Two-segment mode: active vs remainder (occupancy). */
  percentOrValue?: number;
  remainder?: number;
  activeColor?: string;
  activeSegmentLabel?: string;
  remainderSegmentLabel?: string;
};

const HOVER_DARKEN = 0.22;
const HOVER_LIGHTEN = 0.38;
const CELL_STYLE = { cursor: "pointer", transition: "fill 140ms ease-out" } as const;

const DONUT_SIZE = {
  default: { height: 140, innerRadius: 42, outerRadius: 58, centerFontSize: "1.25rem" },
  medium: { height: 170, innerRadius: 52, outerRadius: 74, centerFontSize: "1.5rem" },
  large: { height: 200, innerRadius: 62, outerRadius: 88, centerFontSize: "1.75rem" }
} as const;

/**
 * Compact donut for occupancy % or status counts.
 */
export const ManagementDonutChart: FunctionComponent<ManagementDonutChartProps> = ({
  percentOrValue = 0,
  remainder = 0,
  centerLabel,
  centerSublabel,
  activeColor,
  activeSegmentLabel,
  remainderSegmentLabel,
  valueSuffix,
  size = "default",
  testId,
  segments
}) => {
  const theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const fill = activeColor ?? theme.palette.primary.main;
  const activeLabel = activeSegmentLabel ?? centerSublabel ?? "active";
  const remainderLabel = remainderSegmentLabel ?? "rest";
  const metrics = DONUT_SIZE[size];
  const neutralFill = getManagementNeutralChartFill(theme);
  const tooltipProps = getManagementChartTooltipProps(theme);
  const data =
    segments && segments.length > 0
      ? segments.map(segment => ({
          name: segment.name,
          value: Math.max(segment.value, 0)
        }))
      : [
          { name: activeLabel, value: Math.max(percentOrValue, 0) },
          { name: remainderLabel, value: Math.max(remainder, 0) }
        ];
  const baseFills =
    segments && segments.length > 0 ? segments.map(segment => segment.color) : [fill, neutralFill];

  const getSegmentFill = (index: number) => {
    const base = baseFills[index];
    if (hoveredIndex === null) {
      return base;
    }
    const isDark = theme.palette.mode === "dark";
    if (hoveredIndex === index) {
      return isDark ? lighten(base, HOVER_DARKEN) : darken(base, HOVER_DARKEN);
    }
    return isDark ? darken(base, HOVER_LIGHTEN) : lighten(base, HOVER_LIGHTEN);
  };

  return (
    <Box
      sx={{ position: "relative", width: "100%", height: metrics.height }}
      data-testid={testId ?? "management-donut"}
      data-donut-size={size}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            innerRadius={metrics.innerRadius}
            outerRadius={metrics.outerRadius}
            startAngle={90}
            endAngle={-270}
            stroke="none"
            onMouseEnter={(_, index) => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {baseFills.map((_, index) => (
              <Cell key={data[index].name} fill={getSegmentFill(index)} style={CELL_STYLE} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number) => `${value}${valueSuffix ?? ""}`}
            contentStyle={tooltipProps.contentStyle}
            itemStyle={tooltipProps.itemStyle}
            labelStyle={tooltipProps.labelStyle}
            wrapperStyle={{ zIndex: 1 }}
          />
        </PieChart>
      </ResponsiveContainer>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none"
        }}
      >
        <Typography
          sx={{
            fontFamily: "Fira Code, monospace",
            fontWeight: 600,
            fontSize: metrics.centerFontSize,
            lineHeight: 1.2
          }}
        >
          {centerLabel}
        </Typography>
        {centerSublabel && (
          <Typography variant="caption" color="text.secondary">
            {centerSublabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
